// store/slices/tagSlice.ts
import apiClient from "@/app/utils/apiClient";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

// =================== TAG TYPES ==================
export interface Tag {
  _id: string;
  name: string;
  isActive: boolean;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TagState {
  tags: Tag[];
  loading: boolean;
  error: string | null;
  success: string | null;
}

const initialTagState: TagState = {
  tags: [],
  loading: false,
  error: null,
  success: null,
};

// ================== FETCH PUBLIC TAGS (FOR USERS) ==================
export const fetchTags = createAsyncThunk<Tag[], void, { rejectValue: string }>(
  "tags/fetchTags",
  async (_, { rejectWithValue }) => {
    try {
      // Public endpoint that returns all tags from all admins
      const response = await apiClient.get("/admin/tags");
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch tags",
      );
    }
  },
);

const tagSlice = createSlice({
  name: "tags",
  initialState: initialTagState,
  reducers: {
    clearTagMessages: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Tags
      .addCase(fetchTags.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(fetchTags.fulfilled, (state, action: PayloadAction<Tag[]>) => {
        state.loading = false;
        state.tags = action.payload;
        state.success = "Tags fetched successfully";
      })
      .addCase(fetchTags.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch tags";
      });
  },
});

export const { clearTagMessages } = tagSlice.actions;
export default tagSlice.reducer;
